// 📁 frontend/src/pages/AdminDashboardPage.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { fetchOrders, fetchProducts, fetchCategories, fetchDeliveryZones, fetchCosts } from '../api/api';
import { Pie, Bar } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
Chart.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

function AdminDashboardPage() {
  const admin = JSON.parse(localStorage.getItem('admin'));
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [zones, setZones] = useState([]);
  const [costs, setCosts] = useState([]);

  const [rangeType, setRangeType] = useState('7');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  useEffect(() => {
    (async () => {
      const [o, p, c, z, co] = await Promise.all([
        fetchOrders(),
        fetchProducts(),
        fetchCategories(),
        fetchDeliveryZones(),
        fetchCosts(),
      ]);
      setOrders(o || []);
      setProducts(p || []);
      setCategories(c || []);
      setZones(z || []);
      setCosts(co || []);
    })();
  }, []);

  const startOfDay = useCallback((d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }, []);

  const diffDays = useCallback((a, b) => {
    return Math.floor((startOfDay(a) - startOfDay(b)) / (1000 * 60 * 60 * 24));
  }, [startOfDay]);

  // ✅ Filtered Orders based on filter type
  const filteredOrders = useMemo(() => {
    if (!orders?.length) return [];
    const now = new Date();
    if (rangeType !== 'custom') {
      const days = Number(rangeType);
      return orders.filter(o => diffDays(now, new Date(o.created_at)) < days);
    }
    if (!customStart || !customEnd) return [];
    const s = startOfDay(customStart);
    const e = startOfDay(customEnd);
    return orders.filter(o => {
      const d = startOfDay(new Date(o.created_at));
      return d >= s && d <= e;
    });
  }, [orders, rangeType, customStart, customEnd, diffDays, startOfDay]);

  // ✅ Filtered Costs based on filter type
  const filteredCosts = useMemo(() => {
    if (!costs?.length) return [];
    const now = new Date();
    if (rangeType !== 'custom') {
      const days = Number(rangeType);
      return costs.filter(c => diffDays(now, new Date(c.created_at)) < days);
    }
    if (!customStart || !customEnd) return [];
    const s = startOfDay(customStart);
    const e = startOfDay(customEnd);
    return costs.filter(c => {
      const d = startOfDay(new Date(c.created_at));
      return d >= s && d <= e;
    });
  }, [costs, rangeType, customStart, customEnd, diffDays, startOfDay]);

  const totalSales = filteredOrders.reduce((s, o) => s + (Number(o.total) || 0), 0);
  const totalCosts = filteredCosts.reduce((s, c) => s + Number(c.amount || 0), 0);
  const profit = totalSales - totalCosts;

  const stats = {
    totalOrders: filteredOrders.length,
    pending: filteredOrders.filter(o => o.status === 'pending').length,
    confirmed: filteredOrders.filter(o => o.status === 'confirmed').length,
    delivered: filteredOrders.filter(o => o.status === 'delivered').length,
    canceled: filteredOrders.filter(o => o.status === 'canceled').length,
  };

  const productById = useMemo(() => new Map(products.map(p => [p.id, p])), [products]);
  const categoryById = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories]);

  const last7Labels = ['اليوم', 'أمس', 'قبل يومين', 'قبل 3 أيام', 'قبل 4 أيام', 'قبل 5 أيام', 'قبل 6 أيام'];
  const last7Data = useMemo(() => {
    const now = new Date();
    const buckets = Array(7).fill(0);
    filteredOrders.forEach(o => {
      const d = diffDays(now, new Date(o.created_at));
      if (d >= 0 && d < 7) buckets[d] += Number(o.total) || 0;
    });
    return buckets;
  }, [filteredOrders, diffDays]);

  const productTotals = useMemo(() => {
    const map = new Map();
    filteredOrders.forEach(o => {
      (o.items || []).forEach(it => {
        const pid = it.product_id;
        const qty = Number(it.quantity) || 0;
        map.set(pid, (map.get(pid) || 0) + qty);
      });
    });
    return Array.from(map.entries())
      .map(([pid, qty]) => ({ productName: productById.get(pid)?.name || `#${pid}`, qty }))
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 8);
  }, [filteredOrders, productById]);

  const zoneTotals = useMemo(() => {
    const map = new Map();
    filteredOrders.forEach(o => {
      const zid = o.delivery_zone_id;
      if (zid == null) return;
      map.set(zid, (map.get(zid) || 0) + 1);
    });
    return zones.map(z => ({
      zoneName: z.name,
      count: map.get(z.id) || 0,
    })).sort((a, b) => b.count - a.count);
  }, [filteredOrders, zones]);

  const categoryTotals = useMemo(() => {
    const salesByCat = new Map();
    const ordersByCat = new Map();
    filteredOrders.forEach(o => {
      const catSeenInThisOrder = new Set();
      (o.items || []).forEach(it => {
        const pid = it.product_id;
        const prod = productById.get(pid);
        const catId = prod?.category_id;
        if (!catId) return;
        const itemTotal = Number(it.total_price) || 0;
        salesByCat.set(catId, (salesByCat.get(catId) || 0) + itemTotal);
        if (!catSeenInThisOrder.has(catId)) {
          ordersByCat.set(catId, (ordersByCat.get(catId) || 0) + 1);
          catSeenInThisOrder.add(catId);
        }
      });
    });
    return Array.from(categoryById.keys()).map(cid => ({
      categoryName: categoryById.get(cid)?.name || `#${cid}`,
      ordersCount: ordersByCat.get(cid) || 0,
      sales: salesByCat.get(cid) || 0,
    })).sort((a, b) => b.sales - a.sales);
  }, [filteredOrders, productById, categoryById]);

  return (
    <div style={s.container}>
      <h1 style={s.h1}>📊 لوحة تحكم المشرف</h1>
      <p style={s.welcome}>مرحباً بك، <strong>{admin?.name || 'مشرف'}</strong> 👋</p>

      {/* Filters */}
      <div style={s.filters}>
        <div style={s.quickFilters}>
          <button onClick={() => setRangeType('1')} style={btn(rangeType === '1')}>اليوم</button>
          <button onClick={() => setRangeType('7')} style={btn(rangeType === '7')}>آخر 7 أيام</button>
          <button onClick={() => setRangeType('30')} style={btn(rangeType === '30')}>آخر 30 يوم</button>
          <button onClick={() => setRangeType('custom')} style={btn(rangeType === 'custom')}>مخصص</button>
        </div>
        {rangeType === 'custom' && (
          <div style={s.customRange}>
            <label>من: <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} style={s.dateInput} /></label>
            <label>إلى: <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} style={s.dateInput} /></label>
            <button style={s.applyButton}>تطبيق</button>
          </div>
        )}
      </div>

      {/* Cards */}
      <div style={s.cards}>
        <div style={s.card}><div style={s.cardTitle}>إجمالي الطلبات</div><div style={s.cardValue}>{stats.totalOrders}</div></div>
        <div style={s.card}><div style={s.cardTitle}>إجمالي المبيعات</div><div style={s.cardValue}>{totalSales.toFixed(2)} ₪</div></div>
        <div style={s.card}><div style={s.cardTitle}>إجمالي التكاليف</div><div style={s.cardValue}>{totalCosts.toFixed(2)} ₪</div></div>
        <div style={s.card}><div style={s.cardTitle}>الربح الصافي</div><div style={{...s.cardValue, color: profit >= 0 ? '#16a34a' : '#dc2626'}}>{profit.toFixed(2)} ₪</div></div>
      </div>

      {/* Charts */}
      <div style={s.chartsRow}>
        <div style={s.chartBox}>
          <h3 style={s.boxTitle}>📈 المبيعات لآخر 7 أيام</h3>
          <Bar data={{
            labels: last7Labels,
            datasets: [{ label: 'المبيعات (₪)', data: last7Data, backgroundColor: '#0ea5e9' }]
          }}
            options={{ responsive: true, maintainAspectRatio: false }} />
        </div>

        <div style={s.chartBox}>
          <h3 style={s.boxTitle}>📊 نسب حالات الطلبات</h3>
          <Pie data={{
            labels: ['قيد الانتظار', 'تم التأكيد', 'تم التوصيل', 'ملغاة'],
            datasets: [{
              data: [stats.pending, stats.confirmed, stats.delivered, stats.canceled],
              backgroundColor: ['#fbbf24', '#22c55e', '#2563eb', '#ef4444']
            }]
          }}
            options={{ responsive: true, maintainAspectRatio: false }} />
        </div>
      </div>

      {/* Tables */}
      <div style={s.tablesGrid}>
        {[{
          title: "🍔 أكثر المنتجات مبيعًا (بالكمية)",
          rows: productTotals.map((r, i) => (<tr key={i}><td>{r.productName}</td><td>{r.qty}</td></tr>)),
          emptyCols: 2,
        }, {
          title: "🗂️ المبيعات حسب الفئة",
          rows: categoryTotals.map((r, i) => (<tr key={i}><td>{r.categoryName}</td><td>{r.ordersCount}</td><td>{r.sales.toFixed(2)}</td></tr>)),
          emptyCols: 3,
        }, {
          title: "📍 الطلبات حسب المنطقة",
          rows: zoneTotals.map((r, i) => (<tr key={i}><td>{r.zoneName}</td><td>{r.count}</td></tr>)),
          emptyCols: 2,
        }].map((table, i) => (
          <div key={i} style={s.tableBox}>
            <h3 style={s.boxTitle}>{table.title}</h3>
            <table style={s.table}>
              <thead>
                <tr>{[...Array(table.emptyCols)].map((_, i) => <th key={i}></th>)}</tr>
              </thead>
              <tbody>
                {table.rows.length ? table.rows : (
                  <tr><td colSpan={table.emptyCols} style={s.empty}>لا توجد بيانات</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
/* 🎨 Styles */
const s = {
  container: { direction: 'rtl', fontFamily: "'Cairo', sans-serif", padding: '20px', background: '#fdfdfd', minHeight: '100vh', color: '#222' },
  h1: { margin: '0 0 6px', fontSize: '26px' },
  welcome: { margin: '0 0 20px', color: '#555', fontSize: '16px' },

  filters: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' },
  quickFilters: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  customRange: { display: 'flex', gap: '10px', alignItems: 'center', marginTop: '10px' },
  dateInput: { padding: '6px 10px', border: '1px solid #ddd', borderRadius: '8px' },
  applyButton: { padding: '6px 12px', background: '#0ea5e9', color: '#fff', borderRadius: '8px', border: 'none', cursor: 'pointer' },

  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '12px', marginBottom: '20px' },
  card: { background: '#fff', border: '1px solid #eee', borderRadius: '12px', padding: '14px', boxShadow: '0 2px 6px rgba(0,0,0,0.05)', transition: '0.2s ease' },
  cardTitle: { fontSize: '14px', color: '#555', marginBottom: '6px' },
  cardValue: { fontSize: '20px', fontWeight: 700, color: '#222' },

  chartsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '14px', marginBottom: '24px' },
  chartBox: { background: '#fff', border: '1px solid #eee', borderRadius: '12px', padding: '12px', height: '320px', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' },
  boxTitle: { margin: '0 0 10px', fontSize: '16px' },

  tablesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: '14px', marginBottom: '24px' },
  tableBox: { background: '#fff', border: '1px solid #eee', borderRadius: '12px', padding: '12px', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  empty: { color: '#888', padding: '10px', textAlign: 'center' },
};

function btn(active) {
  return {
    padding: '8px 12px',
    borderRadius: '10px',
    border: active ? `2px solid #0ea5e9` : `1px solid #ddd`,
    background: active ? '#e0f2fe' : '#fff',
    color: '#0f172a',
    cursor: 'pointer',
    fontWeight: 600,
    transition: '0.2s ease',
  };
}

export default AdminDashboardPage;
